// The top bar on every in-game screen: phase, room code, and quick access to your role, your
// notebook and the menu (which also opens the list of characters).
import { useState } from "react";
import type { PlayerView, RoleCard } from "../protocol";
import { seatName } from "../state/selectors";
import { useGame } from "../state/store";
import { CharactersSheet } from "./CharactersSheet";
import { HoldToReveal } from "./HoldToReveal";
import { MenuSheet } from "./MenuSheet";
import { NotebookSheet } from "./Notebook";
import { RoleCardView } from "./RoleCardView";
import { Sheet } from "./Sheet";

export function phaseLabel(view: PlayerView): string {
  switch (view.phase) {
    case "lobby":
      return "Lobby";
    case "setup":
      return "Roles";
    case "first_night":
    case "night":
      return `Night ${view.night_number}`;
    case "day":
      return `Day ${view.day_number}`;
    case "game_over":
      return "Game over";
  }
}

export function PhaseHeader({
  view,
  roles,
  showRole = true,
  showNotebook = true,
}: {
  view: PlayerView;
  roles: Record<string, RoleCard>;
  showRole?: boolean;
  showNotebook?: boolean;
}) {
  const [sheet, setSheet] = useState<"role" | "menu" | "characters" | null>(null);
  const notebookOpen = useGame((s) => s.ui.notebookOpen);
  const setUi = useGame((s) => s.setUi);
  const role = view.private.role;
  const notes = view.private.notebook.length;

  return (
    <header className="row-between" style={{ minHeight: 44 }}>
      <div className="row" style={{ gap: 8 }}>
        <h3>{phaseLabel(view)}</h3>
        <span className="chip mono tiny">{view.room_code}</span>
      </div>
      <div className="row" style={{ gap: 2 }}>
        {showRole && role && (
          <button className="icon-btn" onClick={() => setSheet("role")} aria-label="My role">
            Role
          </button>
        )}
        {showNotebook && view.phase !== "lobby" && (
          <button
            className="icon-btn"
            onClick={() => setUi({ notebookOpen: true })}
            aria-label={`Notebook, ${notes} entries`}
          >
            Notes{notes > 0 && <span className="chip tiny">{notes}</span>}
          </button>
        )}
        <button className="icon-btn" onClick={() => setSheet("menu")} aria-label="Menu">
          ☰
        </button>
      </div>

      <Sheet open={sheet === "role"} onClose={() => setSheet(null)} label="Your role">
        <h3>Your role</h3>
        {role && (
          <HoldToReveal>
            <RoleCardView role={role} alignment={view.private.alignment} />
          </HoldToReveal>
        )}
        {view.private.butler_master_seat_id && (
          <p className="small muted">
            Your master today: {seatName(view, view.private.butler_master_seat_id)}
          </p>
        )}
        {view.private.ghost_vote !== "n/a" && (
          <p className="small muted">
            {view.private.ghost_vote === "available"
              ? "You are dead. You have one ghost vote left."
              : "You are dead and have used your ghost vote."}
          </p>
        )}
        <button className="btn btn-ghost" onClick={() => setSheet(null)}>
          Done
        </button>
      </Sheet>
      <MenuSheet
        open={sheet === "menu"}
        onClose={() => setSheet(null)}
        onCharacters={() => setSheet("characters")}
        view={view}
      />
      <CharactersSheet open={sheet === "characters"} onClose={() => setSheet(null)} view={view} />
      <NotebookSheet
        open={notebookOpen}
        onClose={() => setUi({ notebookOpen: false })}
        view={view}
        roles={roles}
      />
    </header>
  );
}
